import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import {
  canCreateContracts,
  CONTRACT_STATUS_LABELS,
  DIRECTION_LABELS,
  fetchContracts,
  type Contract,
  type ContractDirection,
  type ContractsQuery,
  type ContractStatus,
} from "../lib/contracts";
import { formatDate } from "../lib/format";

/**
 * Реестр договоров (SK-6; отбор и поиск — E17): список из БД. Карточка договора, спецификации и
 * счётчики обязательств — эпики E5/E7. UI на русском, оформление — токены docs/07 (правило 9),
 * даты `ДД.ММ.ГГГГ` (правило 8).
 *
 * ОТБОР И ПОИСК — СЕРВЕРНЫЕ (E17). Клиентский отбор соврал бы в счётчике «Всего»: он показывал бы
 * число отобранных строк, а человек читает его как «столько договоров в системе». Форма панели —
 * по образцу реестра обязательств (ObligationsRegistryPage.tsx): применённый отбор живёт в
 * адресе, поэтому ссылку на срез можно переслать и вернуться к нему «назад».
 *
 * ЭТО НЕ ОГРАНИЧЕНИЕ ВИДИМОСТИ: все роли видят все договоры, отбор человек включает себе сам.
 * Ограничение видимости по Инициатору заказчик перенёс в этап 2 (строка F2-15 бэклога).
 */

type LoadState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly contracts: Contract[] }
  | { readonly kind: "error"; readonly message: string };

/** Значения переключателя «чьи договоры» = значения параметра адреса и запроса. */
const SCOPES = [
  { key: "", label: "Все" },
  { key: "mine", label: "Мои" },
  { key: "unassigned", label: "Не назначен" },
] as const;

type ScopeKey = (typeof SCOPES)[number]["key"];

function scopeOf(raw: string | null): ScopeKey {
  return raw === "mine" || raw === "unassigned" ? raw : "";
}

/**
 * Штамп направления договора. Направление — категория, а не статус, поэтому оба варианта
 * берут нейтральную пару токенов «control» (без зелёного/красного, чтобы не читалось как
 * оценка) и различаются только цветом текста: поставка — акцент, закупка — приглушённый
 * слейт. Пары fg/bg — из docs/07 (правило 9), без «сырых» hex.
 */
function DirectionStamp({ direction }: { direction: ContractDirection }) {
  const cls = direction === "sale" ? "text-accent bg-control-bg" : "text-control-fg bg-control-bg";
  return <span className={`stamp ${cls}`}>{DIRECTION_LABELS[direction]}</span>;
}

/** Штамп статуса договора: действует — зелёная пара «done», архив — приглушённая «waiting». */
function StatusStamp({ status }: { status: ContractStatus }) {
  const cls = status === "active" ? "text-done-fg bg-done-bg" : "text-waiting-fg bg-waiting-bg";
  return <span className={`stamp ${cls}`}>{CONTRACT_STATUS_LABELS[status]}</span>;
}

/**
 * Инициатор в строке реестра (E17). Пустое место — это вопрос без ответа («забыли заполнить?»),
 * поэтому у договора без Инициатора стоит ШТАМП, а не прочерк: «не назначен» — состояние, за
 * которым следуют действия, и его видно как состояние.
 */
function InitiatorCell({ contract }: { contract: Contract }) {
  // Отсутствие поля читаем как «не назначен»: строка реестра не должна ронять весь список.
  const initiator = contract.initiator ?? null;
  if (initiator === null) {
    return <span className="stamp text-waiting-fg bg-waiting-bg">Инициатор не назначен</span>;
  }
  return (
    <span className="text-ink">
      {initiator.fullName}
      {!initiator.isActive && (
        // Учётка деактивирована: договор остаётся закреплённым, но за ним фактически никто не
        // следит — это повод переназначить, а не спрятать имя.
        <span className="ml-1.5 text-[12px] text-muted">(учётка отключена)</span>
      )}
    </span>
  );
}

export function ContractsPage() {
  const { user, logout } = useAuth();
  // Кнопка «Новый договор» — по праву СОЗДАНИЯ (CR-3): оно у всех шести ролей.
  const canRegister = canCreateContracts(user?.roles ?? []);
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  // Применённый отбор — из адреса: ссылку на срез можно переслать, «назад» возвращает к нему.
  const scope = scopeOf(params.get("initiator"));
  const appliedQuery = params.get("q") ?? "";

  // Черновик строки поиска отделён от применённого: печатать можно свободно, запрос уходит по
  // «Найти» (тот же приём, что в реестре обязательств).
  const [draftQuery, setDraftQuery] = useState(appliedQuery);
  useEffect(() => {
    setDraftQuery(appliedQuery);
  }, [appliedQuery]);

  const query = useMemo<ContractsQuery>(
    () => ({
      initiator: scope === "" ? undefined : scope,
      q: appliedQuery === "" ? undefined : appliedQuery,
    }),
    [scope, appliedQuery],
  );

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const result = await fetchContracts(query);
    if (result.ok) {
      setState({ kind: "ready", contracts: result.contracts });
      return;
    }
    // Сессия истекла — сбрасываем клиента на вход (единый механизм через контекст).
    if (result.reason === "unauthorized") {
      await logout();
      return;
    }
    setState({ kind: "error", message: result.message });
  }, [query, logout]);

  useEffect(() => {
    void load();
  }, [load]);

  const apply = (nextScope: ScopeKey, nextQuery: string) => {
    const search = new URLSearchParams();
    if (nextScope !== "") search.set("initiator", nextScope);
    if (nextQuery.trim() !== "") search.set("q", nextQuery.trim());
    setParams(search);
  };

  const filtered = scope !== "" || appliedQuery !== "";

  return (
    <section aria-labelledby="contracts-title">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <h1 id="contracts-title" className="text-[20px] font-bold text-ink">
          Договоры
        </h1>
        <div className="flex items-center gap-3">
          {state.kind === "ready" && (
            <span className="text-[13px] text-muted">
              {filtered ? "Найдено" : "Всего"}: {state.contracts.length}
            </span>
          )}
          {canRegister && (
            <Link
              to="/contracts/new"
              className="rounded-[var(--radius-m)] bg-accent px-3 py-1.5 text-[13px] font-bold text-surface"
            >
              Новый договор
            </Link>
          )}
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1" role="group" aria-label="Чьи договоры показывать">
          {SCOPES.map((s) => {
            const active = scope === s.key;
            return (
              <button
                key={s.key || "all"}
                type="button"
                aria-pressed={active}
                onClick={() => apply(s.key, draftQuery)}
                className={`rounded-[var(--radius-m)] px-3 py-1.5 text-[13px] font-bold ${
                  active ? "bg-accent text-surface" : "border border-border text-ink hover:bg-bg"
                }`}
              >
                {s.label}
              </button>
            );
          })}
        </div>

        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            apply(scope, draftQuery);
          }}
        >
          <label htmlFor="contracts-search" className="sr-only">
            Поиск по номеру, регистрационному номеру Реестра и контрагенту
          </label>
          <input
            id="contracts-search"
            type="search"
            value={draftQuery}
            onChange={(e) => setDraftQuery(e.target.value)}
            placeholder="Номер, номер Реестра или контрагент"
            // Ширина — именованной ступенью Tailwind, а не «сырым» числом: политика ширины
            // экрана и колонок живёт в токенах docs/07 (правило 9), поле ввода к ним не сводится.
            className="w-72 max-w-full rounded-[var(--radius-m)] border border-border bg-surface px-2.5 py-1.5 text-[14px]"
          />
          <button
            type="submit"
            className="rounded-[var(--radius-m)] border border-border px-3 py-1.5 text-[13px] font-bold text-ink hover:bg-bg"
          >
            Найти
          </button>
          {filtered && (
            <button
              type="button"
              onClick={() => apply("", "")}
              className="rounded-[var(--radius-m)] px-2 py-1.5 text-[13px] text-muted hover:underline"
            >
              Сбросить
            </button>
          )}
        </form>
      </div>

      {state.kind === "loading" && (
        <p className="text-muted" role="status">
          Загрузка реестра…
        </p>
      )}

      {state.kind === "error" && (
        <div role="alert" className="rounded-[var(--radius-l)] border border-border bg-surface p-5">
          <p className="text-overdue-fg">{state.message}</p>
          <button
            type="button"
            onClick={() => void load()}
            className="mt-3 rounded-[var(--radius-m)] bg-accent px-3 py-1.5 text-[13px] font-bold text-surface"
          >
            Повторить
          </button>
        </div>
      )}

      {state.kind === "ready" && state.contracts.length === 0 && (
        <div className="rounded-[var(--radius-l)] border border-border bg-surface p-5 text-muted">
          <p className="max-w-[var(--w-text)]">
            {/* Пустой отбор и пустой реестр — разные состояния, и подсказка у них разная:
                в первом случае человеку надо изменить условия, во втором — завести договор. */}
            {scope === "mine"
              ? "За Вами не закреплён ни один договор. Инициатор назначается при регистрации договора; назначить или сменить его может юрист или администратор."
              : scope === "unassigned"
                ? "Договоров без Инициатора нет — у каждого есть ответственный."
                : appliedQuery !== ""
                  ? "По этому запросу ничего не найдено. Поиск идёт по номеру договора, регистрационному номеру Реестра и наименованию контрагента."
                  : "Договоров пока нет. Список наполнится, когда договоры зарегистрируют в системе — через раздел «Регистрация»."}
          </p>
        </div>
      )}

      {state.kind === "ready" && state.contracts.length > 0 && (
        <div className="min-w-fit rounded-[var(--radius-l)] border border-border bg-surface">
          <table className="sticky-head w-full border-collapse text-[14px]">
            <caption className="sr-only">Реестр договоров</caption>
            <thead>
              <tr className="border-b border-border text-left text-[12px] text-muted">
                <th scope="col" className="px-4 py-2.5 font-bold">
                  Номер
                </th>
                <th scope="col" className="px-4 py-2.5 font-bold">
                  Дата
                </th>
                <th scope="col" className="px-4 py-2.5 font-bold">
                  Контрагент
                </th>
                <th scope="col" className="px-4 py-2.5 font-bold">
                  ИНН
                </th>
                <th scope="col" className="px-4 py-2.5 font-bold">
                  Направление
                </th>
                <th scope="col" className="px-4 py-2.5 font-bold">
                  Инициатор
                </th>
                <th scope="col" className="px-4 py-2.5 font-bold">
                  Предмет
                </th>
                <th scope="col" className="px-4 py-2.5 font-bold">
                  Статус
                </th>
              </tr>
            </thead>
            <tbody>
              {state.contracts.map((c) => (
                <tr
                  key={c.id}
                  className="border-b border-border-soft last:border-b-0 align-top hover:bg-bg"
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <Link
                      to={`/contracts/${c.id}`}
                      className="num font-bold text-accent hover:underline"
                    >
                      {c.number}
                    </Link>
                    {c.registryNumber !== null && (
                      // Номер по Реестру заказчика стоит под нашим: в переписке с архивистом
                      // называют именно его, а колонку ради него реестр не удлиняет.
                      <span className="num block text-[12px] text-muted">
                        по Реестру: {c.registryNumber}
                      </span>
                    )}
                  </td>
                  <td className="num px-4 py-3 whitespace-nowrap text-muted">
                    {formatDate(c.date)}
                  </td>
                  <td className="px-4 py-3 text-ink">{c.counterparty.name}</td>
                  <td className="num px-4 py-3 whitespace-nowrap text-muted">
                    {c.counterparty.inn ?? "—"}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <DirectionStamp direction={c.direction} />
                  </td>
                  <td className="px-4 py-3">
                    <InitiatorCell contract={c} />
                  </td>
                  <td className="px-4 py-3 text-muted">{c.subject ?? "—"}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <StatusStamp status={c.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
